/**
 * Telemetry Service - Lightweight event tracking for ScopeOps Scoping
 * No PII captured - only behavioral events with minimal props
 */

// Event types
export const TELEMETRY_EVENTS = {
  // Product Mode
  PRODUCT_MODE_VIEW: 'productMode.view',
  
  // Navigation
  NAV_CLICK: 'nav.click',
  ROUTE_GUARD_REDIRECT: 'route.guard_redirect',
  
  // Inspector
  INSPECTOR_OPEN: 'inspector.open',
  INSPECTOR_TAB_CLICK: 'inspector.tab_click',
  INSPECTOR_PO_VIEW_ENABLED: 'inspector.po_view_enabled',
  INSPECTOR_LINKED_TAB_OPENED: 'inspector.linked_tab_opened',  // New: Governance links
  
  // Assistant
  ASSISTANT_OPEN: 'assistant.open',
  SUPPORT_AI_ROLE_SELECTED: 'support_ai.role_selected',
  
  // Services Request
  SERVICES_REQUEST_SUBMITTED: 'services.request_submitted',
  
  // Partner Tenant Mode
  PARTNER_PORTFOLIO_VIEW_OPENED: 'partner.portfolio_view_opened',
  PARTNER_EXPORT_BUNDLE_GENERATED: 'partner.export_bundle_generated',
  
  // Governance Relationships Linking
  GOV_LINK_ADDED: 'gov.link_added',
  GOV_LINK_REMOVED: 'gov.link_removed',
  GOV_ROLLUP_VIEW_OPENED: 'gov.rollup_view_opened',
  GOV_SELECTOR_OPENED: 'gov.selector_opened',
  GOV_SELECTOR_SEARCHED: 'gov.selector_searched',
  GOV_SELECTOR_CONFIRMED: 'gov.selector_confirmed',
  GOV_HOTSPOTS_OPENED: 'gov.hotspots_opened',
  GOV_HOTSPOT_CLICKED: 'gov.hotspot_clicked',
  
  // Governance Grid & Roadmap Indicators
  GOV_GRID_POPOVER_OPENED: 'gov.grid_popover_opened',
  GOV_GRID_OPEN_IN_INSPECTOR: 'gov.grid_open_in_inspector',
  GOV_ROADMAP_WARNING_HOVER: 'gov.roadmap_warning_hover',
  GOV_ROADMAP_OPEN_IN_INSPECTOR: 'gov.roadmap_open_in_inspector',
  INSPECTOR_LINKED_PANEL_AUTOSCROLL: 'inspector.linked_panel_autoscroll',
  
  // Scoping Governance (Phase 1)
  SCOPING_DECISION_LINK_ADDED: 'scoping.decision_link_added',
  SCOPING_DECISION_LINK_REMOVED: 'scoping.decision_link_removed',
  SCOPING_BASELINE_PROPOSE_FROM_DECISION: 'scoping.baseline_propose_from_decision',
  SCOPING_DRIFT_VIEW_OPENED: 'scoping.drift_view_opened',
  SCOPING_DRIFT_ITEM_CLICKED: 'scoping.drift_item_clicked',
  SCOPING_RISK_LINK_ADDED: 'scoping.risk_link_added',
  SCOPING_RISK_LINK_REMOVED: 'scoping.risk_link_removed',
  SCOPING_DOC_ATTACHED: 'scoping.doc_attached',
  SCOPING_DOC_DOWNLOADED: 'scoping.doc_downloaded',
  
  // Scoping Governance (Phase 2)
  SCOPING_DOC_VERSION_UPLOADED: 'scoping.doc_version_uploaded',
  SCOPING_DOC_VERSION_VIEWED: 'scoping.doc_version_viewed',
  SCOPING_DOC_VERSION_DOWNLOADED: 'scoping.doc_version_downloaded',
  SCOPING_SIGNOFF_REQUESTED: 'scoping.signoff_requested',
  SCOPING_SIGNOFF_STATUS_UPDATED: 'scoping.signoff_status_updated',
  SCOPING_ESIGN_REQUEST_SENT: 'scoping.esign_request_sent',
  SCOPING_SMART_SUGGEST_SHOWN: 'scoping.smart_suggest_shown',
  SCOPING_SMART_SUGGEST_ACCEPTED: 'scoping.smart_suggest_accepted',
  SCOPING_SMART_SUGGEST_DISMISSED: 'scoping.smart_suggest_dismissed',
  
  // AI ScopeOps - Acceptance / Benefits / KPIs
  AI_ACCEPTANCE_SUGGEST_REQUESTED: 'ai.acceptance_suggest_requested',
  AI_ACCEPTANCE_SAVED: 'ai.acceptance_saved',
  AI_KPI_SUGGEST_REQUESTED: 'ai.kpi_suggest_requested',
  AI_KPI_SAVED: 'ai.kpi_saved',
  AI_BENEFITS_SUGGEST_REQUESTED: 'ai.benefits_suggest_requested',
  AI_BENEFIT_LINK_SAVED: 'ai.benefit_link_saved',
  AI_KPI_LINK_SAVED: 'ai.kpi_link_saved',
  AI_CHECKPOINT_SUMMARY_REQUESTED: 'ai.checkpoint_summary_requested',
  AI_CHECKPOINT_SUMMARY_VIEWED: 'ai.checkpoint_summary_viewed',
  
  // Proactive AI Suggestions
  AI_PROACTIVE_SUGGESTION_SHOWN: 'ai.proactive_suggestion_shown',
  AI_PROACTIVE_SUGGESTION_ACCEPTED: 'ai.proactive_suggestion_accepted',
  AI_PROACTIVE_SUGGESTION_REJECTED: 'ai.proactive_suggestion_rejected',
  AI_PROACTIVE_SUGGESTION_DEFERRED: 'ai.proactive_suggestion_deferred',
  
  // Scoping Tier
  TIER_UPGRADE_PROMPT_SHOWN: 'tier.upgrade_prompt_shown',
  TIER_UPGRADE_CLICKED: 'tier.upgrade_clicked',
  
  // Templates & CTA
  CTA_CLICK: 'cta_click',
  TEMPLATE_DOWNLOAD: 'template_download',
  
  // Consulting Mode
  CONSULTING_RATECARD_OPENED: 'consulting.ratecard_opened',
  CONSULTING_RATECARD_SAVED: 'consulting.ratecard_saved',
  CONSULTING_ESTIMATE_GENERATED: 'consulting.estimate_generated',
};

// In-memory event buffer for batching
let eventBuffer = [];
let flushTimeout = null;
const FLUSH_INTERVAL = 5000; // 5 seconds
const MAX_BUFFER_SIZE = 20;

/**
 * Track a telemetry event
 * @param {string} eventName - Event name from TELEMETRY_EVENTS
 * @param {object} props - Event properties (no PII)
 */
export const trackEvent = (eventName, props = {}) => {
  const event = {
    event: eventName,
    timestamp: new Date().toISOString(),
    props: {
      ...props,
      // Add session context
      sessionId: getSessionId(),
    },
  };
  
  // Add to buffer
  eventBuffer.push(event);
  
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[Telemetry]', eventName, props);
  }
  
  // Flush if buffer is full
  if (eventBuffer.length >= MAX_BUFFER_SIZE) {
    flushEvents();
  } else if (!flushTimeout) {
    // Schedule flush
    flushTimeout = setTimeout(flushEvents, FLUSH_INTERVAL);
  }
};

/**
 * Flush events to backend
 */
const flushEvents = async () => {
  if (eventBuffer.length === 0) return;
  
  const events = [...eventBuffer];
  eventBuffer = [];
  
  if (flushTimeout) {
    clearTimeout(flushTimeout);
    flushTimeout = null;
  }
  
  try {
    // Send to backend (fire and forget)
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${BACKEND_URL}/api/telemetry/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('simpliview_token')}`,
      },
      body: JSON.stringify({ events }),
    }).catch(() => {
      // Silently fail - telemetry should never break the app
    });
  } catch (e) {
    // Silently fail
  }
};

/**
 * Get or create session ID
 */
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('sv_session_id');
  if (!sessionId) {
    sessionId = `s_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('sv_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Track page view with product mode
 */
export const trackPageView = (pagePath, productMode) => {
  trackEvent(TELEMETRY_EVENTS.PRODUCT_MODE_VIEW, {
    productMode,
    pagePath,
  });
};

/**
 * Track navigation click
 */
export const trackNavClick = (path, productMode) => {
  trackEvent(TELEMETRY_EVENTS.NAV_CLICK, {
    productMode,
    path,
  });
};

/**
 * Track route guard redirect
 */
export const trackRouteGuard = (fromRoute, toRoute, productMode) => {
  trackEvent(TELEMETRY_EVENTS.ROUTE_GUARD_REDIRECT, {
    productMode,
    fromRoute,
    toRoute,
  });
};

/**
 * Track inspector open
 */
export const trackInspectorOpen = (entityType, productMode) => {
  trackEvent(TELEMETRY_EVENTS.INSPECTOR_OPEN, {
    productMode,
    entityType,
  });
};

/**
 * Track inspector tab click
 */
export const trackInspectorTabClick = (tab, entityType, productMode) => {
  trackEvent(TELEMETRY_EVENTS.INSPECTOR_TAB_CLICK, {
    productMode,
    entityType,
    tab,
  });
};

/**
 * Track assistant open
 */
export const trackAssistantOpen = (context, productMode) => {
  trackEvent(TELEMETRY_EVENTS.ASSISTANT_OPEN, {
    productMode,
    context,
  });
};

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushEvents);
}

export default {
  trackEvent,
  trackPageView,
  trackNavClick,
  trackRouteGuard,
  trackInspectorOpen,
  trackInspectorTabClick,
  trackAssistantOpen,
  TELEMETRY_EVENTS,
};
